import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form, FormControl, Button, InputGroup } from 'react-bootstrap';
import CustomBackground from '../../components/UI/CustomBackground/CustomBackground';
import { useTranslation, Translation } from 'react-i18next';
import graphQLClientInstance from '../../axios-graphql-client';
import classes from './Contributions.module.css';
import Table, { tableOrigin } from '../../components/UI/Table/Table';
import { ReactComponent as InfoCircleLightIcon } from '../../assets/infoCircleLight.svg';
import { Formik } from "formik";
import * as Yup from "yup";
import { withRouter } from 'react-router-dom';
import Loader from '../../components/UI/Loader/Loader';
import {
    backgroundImages,
    API_URLS,
    renderTooltipOverlay,
    contributionsReturnTypes,
    createSubscriptionFromValues,
    CONTRIBUTION_COUNTRIES_SESSION_KEY,
    getUserId,
    calcFileSize,
    BOOTSTRAP_CLASSES,
  } from '../../shared/utility';
import SearchFormContainer from '../../components/UI/SearchFormContainer/SearchFormContainer';
import { useAlert } from '../../shared/AlertService';

import { useMatomo } from '@datapunt/matomo-tracker-react';
import * as XmlJs from 'xml-js';

const Contributions = (props: any) => {
  const { trackPageView } = useMatomo();

  const { t, i18n } = useTranslation();

  const [formValues, setFormValues] = useState([]);
  const formValuesRef = React.useRef({});
  formValuesRef.current = formValues;

  const [isLoading, setIsLoading] = useState(false);
  const [createNewFile, setCreateNewFile] = useState(false);
  const createNewFileRef = React.useRef(false);
  createNewFileRef.current = createNewFile;

  const [countriesOptions, setCountriesOptions] = useState<IContributionCountryOption[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [pageSize, setPageSize] = useState(0);
  const pageSizeRef = React.useRef(20)
  pageSizeRef.current = pageSize;

  const [pageIndex, setPageIndex] = useState(0);
  const pageIndexRef = React.useRef(0)
  pageIndexRef.current = pageIndex;

  const [uploadFile, setUploadFile] = useState<any>(null);
  const resultsRef = React.useRef(null);
  const [data, setData] = useState<any[]|[]>([]);
  const dataRef = React.useRef<any[]>();
  dataRef.current = data;

  const alert = useAlert();

  const formInitialValues: {[key: string]: any} = {
    countryid: "",
    filename: "",
    comment: ""
  };

  const [initialValues, setInitialValues] = useState<{[key: string]: any}>(formInitialValues);

  useEffect(() => {
    trackPageView({documentTitle:'Contributions'});
    window.scrollTo({top: 0, behavior: 'smooth'});
  }, []);

  useEffect(() => {
    const sessionCountries = sessionStorage.getItem(CONTRIBUTION_COUNTRIES_SESSION_KEY);
    if (sessionCountries) {
      setCountriesOptions(JSON.parse(sessionCountries));
    } else {
      getContributionCountries(setCountriesOptions);
    }
  }, []);

  const fetchData = React.useCallback(({ pageIndex: tablePageIndex, pageSize: tablePageSize }) => {
    if (createNewFileRef.current) {
      return;
    }
    setIsLoading(true);
    setData([]);
    setPageSize(tablePageSize);
    setPageIndex(tablePageIndex);

    if (tablePageIndex === 0) {
      setPageCount(0);
      setTotalCount(0);
    }

    graphQLClientInstance.get(`${API_URLS.CONTRIBUTIONS_LIST}?limit=${tablePageSize}&offset=${tablePageIndex * tablePageSize}`)
    .then(res => {
      if (res.data?.data?.length) {
        setData(res.data.data);
        setTotalCount(res.data.data[0].totals);
        setPageCount(Math.ceil(res.data.data[0].totals/tablePageSize));
      }
      setIsLoading(false);
    })
    .catch(err => {
      console.log(err);
      setIsLoading(false);
    })
  }, []);

  const inputReference = React.useRef<HTMLInputElement|null>(null)

  const browseFile = () => {
    if (inputReference.current) {
      (inputReference.current as HTMLInputElement).click();
    }
  }

  const refreshData = React.useCallback(() => {
    fetchData({
      pageIndex: pageIndexRef.current,
      pageSize: pageSizeRef.current,
      values: formValuesRef.current
    });
  }, []);

  const clearFile = () => {
    if (inputReference.current) {
      (inputReference.current as HTMLInputElement).value = "";
    }
  }

  const form = (
    <Formik
      enableReinitialize = {true}
      initialValues = {initialValues}
      validationSchema = {Yup.object()
      .shape({
        countryid: Yup.string().required(t("validation_errors.required")),
        filename: Yup.string().required(t("validation_errors.required")),
      })
    }

    onSubmit = {(values: any, { setSubmitting, resetForm }) => {
        const payload = {
            ...values
        };
        setSubmitting(false);
        setCreateNewFile(true);
        setFormValues(payload);
        setIsLoading(true);
        graphQLClientInstance.post(API_URLS.CONTRIBUTIONS_LIST, createSubscriptionFromValues(values))
          .then(async (res) => {
             if (res.data?.url) {

              const uploadData = new FormData;
              for (const [fieldKey, fieldValue] of Object.entries(res.data?.fields)) {
                uploadData.append(fieldKey, fieldValue as string);
              }
              uploadData.append('X-Amz-Meta-country_id', values.countryid);
              uploadData.append('X-Amz-Meta-user_id', getUserId());
              uploadData.append('X-Amz-Meta-comment', res.data.comment);
              uploadData.append('file', uploadFile);

              await graphQLClientInstance.post(res.data.url, uploadData, {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              }).then((uploadRes) => {
                setIsLoading(false);
                alert({
                  catchOnCancel: false,
                  title: t("info"),
                  body: t("file_uploaded"),
                });
              }).catch((uploadErr) => {
                console.log(uploadErr);
                setIsLoading(false);
                if(uploadErr?.response?.data) {
                  const xmlToJson = JSON.parse(XmlJs.xml2json(uploadErr.response.data, {compact: true, spaces: 4}));
                  const errorText =
                    (xmlToJson?.Error?.MaxSizeAllowed)?(
                      <span>
                        {xmlToJson.Error.Message?._text}.<br />
                        {t("error_messages.max_size_allowed", {size: calcFileSize(parseInt(xmlToJson.Error.MaxSizeAllowed?._text))})}
                      </span>
                    ):xmlToJson?.Error?.Message?._text
                  alert({
                    catchOnCancel: false,
                    title: t("alert"),
                    body: errorText,
                  });
                } else {
                  alert({
                    catchOnCancel: false,
                    title: t("alert"),
                    body: uploadErr.response,
                  });
                }
              })
            } else {
              setIsLoading(false);
            }
          })
          .catch(err => {
            setIsLoading(false);
            console.log(err);
          })
          .finally(() => {
            clearFile();
            resetForm();
            setCreateNewFile(false);
          })
      }
    }
    >

    {formProps => (
      <Form
        className={classes.searchForm}
        onSubmit={(e) => {
          formProps.handleSubmit();
          e.preventDefault();
        }}>
        <Row className={classes.searchFieldsRow}>
          <Col md="4" id="countryIdContainer">
            <Form.Group controlId="searchForm.countryId">
              <Form.Label className="w-100">
                {t("country")}
                {renderTooltipOverlay(
                  t("tooltips_contributor_portal.country"),
                  <InfoCircleLightIcon />
                )}
              </Form.Label>
              <Form.Control
                as="select"
                name="countryid"
                className={formProps.errors.countryid?BOOTSTRAP_CLASSES.IS_INVALID:""}
                value={formProps.values.countryid}
                onChange={formProps.handleChange}
              >
                <option value={""}>{t('select')}</option>
                {
                  countriesOptions?.map((country: IContributionCountryOption, index: number) => {
                    return (
                      <option key={`contribution-country-option-${index}`} value={country.country_id}>
                        {country.country_name}
                      </option>
                    )
                  })
                }
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {formProps.errors.countryid}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md="4" id="contributionFileContainer">
            <Form.Label className="w-100">
              {t("contribution_file")}
              {renderTooltipOverlay(
                t("tooltips_contributor_portal.contribution_file"),
                <InfoCircleLightIcon />
              )}
            </Form.Label>
            <input type="file" ref={inputReference} hidden onChange={(e: any) => {
                if (e.target.files?.length) {
                  setUploadFile(e.target.files[0]);
                  formProps.setFieldValue("filename", e.target.files[0].name)
                }
              }}  />
            <InputGroup>
              <InputGroup.Prepend>
                <Button
                  variant="outline-secondary"
                  style={{borderRadius: 0}}
                  className={formProps.errors.filename?BOOTSTRAP_CLASSES.IS_INVALID:""}
                  onClick={() => browseFile()}>
                    {t("browse")}
                </Button>
              </InputGroup.Prepend>
              <FormControl
                readOnly
                value={formProps.values.filename}
                className={formProps.errors.filename?BOOTSTRAP_CLASSES.IS_INVALID:""}
                style={{background: "#FFFFFF", cursor: "pointer"}}
                onClick={() => browseFile()}/>
              <Form.Control.Feedback type="invalid">
                {formProps.errors.filename}
              </Form.Control.Feedback>
            </InputGroup>
          </Col>
          <Col md="12" id="commentContainer">
            <Form.Group controlId="searchForm.commentContainer">
              <Form.Label className="w-100">
                {t("comment")}
                {renderTooltipOverlay(
                  t("tooltips_contributor_portal.comment"),
                <InfoCircleLightIcon />)}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="comment"
                maxLength={1000}
                style={{resize: 'none'}}
                value={formProps.values.comment}
                onChange={formProps.handleChange}
              />
              <span className={classes.formControlCharacterCounter}>
                {formProps.values.comment.length}/1000
              </span>
            </Form.Group>
          </Col>
        </Row>
        <Row className={classes.actionsRow}>
          <Col md="12">
            <Button id="submitSearchForm" variant="primary" type="submit" disabled={formProps.isSubmitting} className="float-right">{t("create")}</Button>
            <Button
              id="clearFields"
              variant="link"
              onClick={() => {
                setInitialValues(formInitialValues);
                clearFile();
                formProps.resetForm();
              }}
              className="float-right mr-2">{t("clear_fields")}</Button>
            <Button
              id="help"
              variant="link"
              onClick={() => window.open(`help/${i18n.language}/Contributor_portal.html`, '_blank')}
              className="float-right mr-2">
                {t("help")}
            </Button>
          </Col>
        </Row>
      </Form>
    )}
    </Formik>
  );

  const selectedColumns = [
    {
      name: contributionsReturnTypes.COUNTRY,
      label: <Translation>{(translate) =>  translate('country')}</Translation>,
      enabled: true,
      isDefault: true,
      disableSortBy: true,
      defaultOrder: 0
    },
    {
      name: contributionsReturnTypes.UPLOAD_DATE,
      label: <Translation>{(translate) =>  translate('upload_date')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 1,
    },
    {
      name: contributionsReturnTypes.UPLOADER,
      label: <Translation>{(translate) =>  translate('uploader')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 2,
    },
    {
      name: contributionsReturnTypes.FILE_NAME,
      label: <Translation>{(translate) =>  translate('file_name')}</Translation>,
      enabled: true,
      isDefault: true,
      disableSortBy: true,
      defaultOrder: 3
    },
    {
      name: contributionsReturnTypes.FILE_COMMENT,
      label: <Translation>{(translate) =>  translate('comment')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 4,
    },
  ];

   const tableColumns = React.useMemo(() => {
    return [
      ...selectedColumns.map((item: any) => { return {...item, Header: item.label, accessor: item.name}})
    ]
  }, [selectedColumns]);

  return (
    <>
    <Loader show={isLoading}></Loader>
      <CustomBackground  image={backgroundImages.MY_TESTS}>
        <SearchFormContainer
          title={t("contributions_list.title")}
          subtitle={t("contributions_list.subtitle")}
          >
          {form}
        </SearchFormContainer>
      </CustomBackground>
      <Container fluid style={{display: !(isLoading && totalCount === 0) || createNewFile?'block':'none'}} className={classes.resultsTable}>
        <Table
          tableRef={resultsRef}
          columns={tableColumns}
          origin={tableOrigin.CONTRIBUTIONS}
          hideColumnsButton={true}
          isSortable={false}
          data={data}
          fetchData={fetchData}
          totalCount={totalCount}
          pageCount={pageCount}
          refreshData={refreshData}
          hideDownloadExcelButton={true}
          formValues={formValues}
          showPagination={true}
          pageSizeOptions={[5, 10, 20, 25, 50, 100]}
        />
      </Container>
    </>
  )
}

const getContributionCountries = (setCountriesOptions: any) => {
  graphQLClientInstance.get(API_URLS.CONTRIBUTIONS_COUNTRIES).then((res: any) => {
    sessionStorage.setItem(CONTRIBUTION_COUNTRIES_SESSION_KEY, JSON.stringify(res.data));
    setCountriesOptions(res.data);
  }).catch((err: any) => { console.log(err); } )
}

export default withRouter(Contributions);

export interface IContributionCountryOption {
  country_id: string;
  country_name: string;
}
